// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { UpdateClinicSettingsType } from "../../types";
import { updateClinicSettings } from "../../api";

export const useUpdateClinicSettings = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (clinicData: UpdateClinicSettingsType) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateClinicSettings(token, clinicId, clinicData);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate clinics query cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinics", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("Settings updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Settings could not be updated!"),
      });
    },
  });
};
